/**
 * DiscoverCommunitiesWidget - Display random non-member communities with refresh
 * Reusable component for both desktop and mobile layouts.
 * Logic matches desktop CommunityList: shuffles non-member communities, shows 10.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Compass, Users } from 'lucide-react';
import type { ThemeTokens } from '../../../shared/theme/tokens';
import { getCommunities } from '../../../lib/api-client';

interface DiscoverCommunitiesWidgetProps {
  theme: ThemeTokens;
  className?: string;
  style?: React.CSSProperties;
  maxDisplay?: number;
  showHeader?: boolean;
  compact?: boolean;
  onNavigate?: (path: string) => void;
}

interface WidgetCommunity {
  id: string;
  name: string;
  nameSlug?: string;
  slug?: string;
  description?: string;
  avatar?: string;
  memberCount: number;
  isMember: boolean;
}

export const DiscoverCommunitiesWidget: React.FC<DiscoverCommunitiesWidgetProps> = ({
  theme,
  className = '',
  style = {},
  maxDisplay = 10,
  showHeader = true,
  compact = false,
  onNavigate,
}) => {
  const navigate = onNavigate || ((path: string) => { window.location.href = path; });
  const [allNonMember, setAllNonMember] = useState<WidgetCommunity[]>([]);
  const [discover, setDiscover] = useState<WidgetCommunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Shuffle and pick random non-member communities (same logic as desktop CommunityList)
  const shuffle = useCallback((items: WidgetCommunity[]) => {
    const shuffled = [...items].sort(() => 0.5 - Math.random());
    setDiscover(shuffled.slice(0, maxDisplay));
  }, [maxDisplay]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const all = await getCommunities() as any[];
        const nonMember = all.filter((c: any) => !c.isMember);
        setAllNonMember(nonMember);
        shuffle(nonMember);
      } catch {
        setAllNonMember([]);
        setDiscover([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    shuffle(allNonMember);
  };

  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      style={{
        backgroundColor: theme.cardBg || 'rgba(255, 255, 255, 0.05)',
        borderColor: `${theme.accentColor}40`,
        border: '1px solid',
        ...style,
      }}
    >
      {showHeader && (
        <div
          className={`flex items-center justify-between border-b ${compact ? 'px-3 py-2' : 'px-4 py-3'}`}
          style={{ borderColor: `${theme.accentColor}40` }}
        >
          <h3 className="font-bold flex items-center gap-2" style={{ color: theme.textColor }}>
            <Compass size={16} />
            Discover
          </h3>
          <button
            onClick={handleRefresh}
            className="rounded-lg transition-all hover:opacity-70 p-1.5"
            style={{ backgroundColor: theme.accentColor + '30', color: theme.textColor }}
            title="Refresh communities"
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
          </button>
        </div>
      )}

      <div className={compact ? 'p-2' : 'p-4'}>
        {isLoading ? (
          <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
            Loading...
          </div>
        ) : discover.length > 0 ? (
          <div className="space-y-3">
            {discover.map((c) => (
              <div
                key={c.id}
                onClick={() => navigate(`/community/${c.nameSlug || c.slug || c.id}`)}
                className="flex items-start gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                style={{ backgroundColor: theme.accentColor + '10' }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: c.avatar ? `url(${c.avatar}) center/cover` : theme.accentColor + '40',
                  }}
                >
                  {!c.avatar && <Users size={18} style={{ color: theme.textColor }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate text-sm" style={{ color: theme.textColor }}>
                    {c.name}
                  </p>
                  <p className="text-xs truncate" style={{ color: theme.textColor + '99' }}>
                    {c.memberCount} members
                  </p>
                  {c.description && (
                    <p className="text-xs mt-1 line-clamp-2" style={{ color: theme.textColor + '99' }}>
                      {c.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-6">
            <Compass size={48} style={{ color: theme.textColor + '40', marginBottom: '12px' }} />
            <p style={{ color: theme.textColor + '99', fontSize: '14px', marginBottom: '8px' }}>
              No communities to discover
            </p>
            <p style={{ color: theme.textColor + '66', fontSize: '12px' }}>
              You've joined all available communities!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
